import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Plus, TriangleAlert, Users } from 'lucide-react';
import { z } from 'zod';
import { getErrorMessage, keys } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { formatDate, formatDateTime, hasPermission, ROLE_LABELS } from '@blimo/shared';
import {
  applyProblemToForm,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  EmptyState,
  Form,
  FormField,
  FormItem,
  FormMessage,
  FormRootError,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@blimo/ui';
import { PartyLink } from '../../components/data.jsx';
import { PartyPicker } from '../meters/party-picker.jsx';

const memberSchema = z.object({ party_id: z.string().min(1, 'Bitte eine Person auswählen.') });

/**
 * Mitglieder (member_of_client): Personen, deren Portal-Benutzer die Einheiten des Mandanten sehen.
 * @param {{ client: any, canEdit: boolean }} props
 */
export function MembersTab({ client, canEdit }) {
  const api = useApi();
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [removing, setRemoving] = useState(/** @type {any} */ (null));
  const members = client.members ?? [];
  const canSeeUsers = hasPermission(me, 'users.view');
  const refresh = () => queryClient.invalidateQueries({ queryKey: keys.admin.resource('mandates') });

  async function remove() {
    try {
      await api.admin.mandates.removeMember(client.id, removing.party.id);
      await refresh();
      toast.success('Mitglied entfernt');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Das Mitglied konnte nicht entfernt werden.'));
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-2">
        <div className="flex flex-col gap-1.5">
          <CardTitle as="h2">Mitglieder</CardTitle>
          <CardDescription>Personen dieses Mandanten. Ihr Portal-Zugang braucht die Rolle „Investor“, damit die Einheiten sichtbar werden.</CardDescription>
        </div>
        {canEdit && (
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus aria-hidden="true" /> Mitglied hinzufügen
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {members.length === 0 ? (
          <EmptyState compact icon={Users} title="Keine Mitglieder" description="Ohne Mitglieder sieht niemand die Einheiten dieses Mandanten im Portal." />
        ) : (
          <Table aria-label="Mitglieder">
            <TableHeader>
              <TableRow>
                <TableHead>Person</TableHead>
                <TableHead>Portal-Zugang</TableHead>
                <TableHead>Rollen</TableHead>
                <TableHead>Letzte Anmeldung</TableHead>
                {canEdit && <TableHead className="text-right">Aktion</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((m) => (
                <TableRow key={m.party.id}>
                  <TableCell>
                    <span className="flex flex-col">
                      <PartyLink party={{ id: m.party.id, display_name: m.party.name }} />
                      {m.party.email && <span className="text-xs break-all text-muted-foreground">{m.party.email}</span>}
                      {!m.active && <Badge variant="neutral">Beendet {m.valid_to ? formatDate(m.valid_to) : ''}</Badge>}
                    </span>
                  </TableCell>
                  <TableCell>
                    {m.user ? (
                      <span className="flex flex-col items-start gap-1">
                        {canSeeUsers ? (
                          <Link to="/administration/benutzer/$userId" params={{ userId: String(m.user.id) }} className="break-all text-primary hover:underline">
                            {m.user.email}
                          </Link>
                        ) : (
                          <span className="break-all">{m.user.email}</span>
                        )}
                        <StatusBadge domain="user" status={m.user.status} />
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Kein Benutzerkonto</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {m.user ? (
                      <span className="flex flex-wrap gap-1">
                        {m.user.roles.map((r) => (
                          <Badge key={r} variant="secondary">
                            {ROLE_LABELS[r] ?? r}
                          </Badge>
                        ))}
                        {!m.user.is_investor && (
                          <Badge variant="warning">
                            <TriangleAlert aria-hidden="true" className="size-3" /> keine Investor-Rolle
                          </Badge>
                        )}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">–</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="numeric">{m.user?.last_login_at ? formatDateTime(m.user.last_login_at) : m.user ? 'nie' : '–'}</span>
                  </TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => setRemoving(m)} aria-label={`${m.party.name} entfernen`}>
                        Entfernen
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
      {canEdit && (
        <>
          <Sheet open={addOpen} onOpenChange={setAddOpen}>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>Mitglied hinzufügen</SheetTitle>
                <SheetDescription>{client.name}</SheetDescription>
              </SheetHeader>
              {addOpen && <AddMemberForm clientId={client.id} onDone={() => setAddOpen(false)} onAdded={refresh} />}
            </SheetContent>
          </Sheet>
          <ConfirmDialog
            open={Boolean(removing)}
            onOpenChange={(open) => !open && setRemoving(null)}
            title="Mitglied entfernen?"
            description={removing ? `${removing.party.name} sieht die Einheiten von ${client.name} danach nicht mehr im Kundenportal.` : undefined}
            confirmLabel="Entfernen"
            destructive
            onConfirm={remove}
          />
        </>
      )}
    </Card>
  );
}

function AddMemberForm({ clientId, onDone, onAdded }) {
  const api = useApi();
  const form = useForm({ resolver: zodResolver(memberSchema), defaultValues: { party_id: '' } });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await api.admin.mandates.addMember(clientId, values.party_id);
      await onAdded();
      toast.success('Mitglied hinzugefügt');
      onDone();
    } catch (error) {
      applyProblemToForm(error, form.setError, { fields: ['party_id'] });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} noValidate className="flex flex-1 flex-col gap-4 px-4">
        <FormRootError />
        <FormField
          name="party_id"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <PartyPicker label="Person" value={field.value} emptyLabel="Bitte Person suchen …" onChange={(id) => field.onChange(id)} />
              <FormMessage />
            </FormItem>
          )}
        />
        <SheetFooter className="px-0">
          <Button type="submit" loading={form.formState.isSubmitting}>
            Hinzufügen
          </Button>
          <Button type="button" variant="outline" onClick={onDone}>
            Abbrechen
          </Button>
        </SheetFooter>
      </form>
    </Form>
  );
}
